import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { usd } from "@/lib/usd";
import { useAuth } from "@/context/AuthContext";
import { CurrentPlan } from "@/components/billing/CurrentPlan";
import { ChangePlanDialog } from "@/components/billing/ChangePlanDialog";

const OPTIONS = [["free", "Free"], ["pro", "Pro"], ["commercial", "Commercial"]];

export const AccountPlan = () => {
  const { reload } = useAuth();
  const qc = useQueryClient();
  const { data: b } = useQuery({ queryKey: ["billing"], queryFn: () => api.get("/billing/me").then((r) => r.data) });
  const [interval, setPeriod] = useState("year");
  const [change, setChange] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (b?.subscription?.interval) setPeriod(b.subscription.interval); }, [b?.subscription?.interval]);
  if (!b) return <Loader2 className="h-4 w-4 animate-spin text-[#A8A090]" />;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["billing"] }); reload(); };
  const sub = b.subscription;
  const subscribed = b.base_source === "subscription";
  const fixed = ["admin", "granted"].includes(b.base_source) || b.lifetime === "commercial";
  const price = (t) => (t === "free" ? 0 : b.prices[`${t === "pro" ? "" : "commercial_"}${interval === "year" ? "yearly" : "monthly"}`]);
  const isCurrent = (t) => (subscribed ? !sub?.features && sub?.tier === t && sub?.interval === interval
    : (b.base_tier || "free") === t && t === "free");
  const pick = async (t) => {
    if (subscribed) return setChange(t === "free" ? { tier: "free" } : { tier: t, interval });
    if (t === "free") return;
    setBusy(t);
    try { const { data } = await api.post("/billing/checkout", { tier: t, interval, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  return (
    <div className="space-y-5" data-testid="account-plan">
      {(b.pro || b.base_source) ? <CurrentPlan b={b} onChanged={refresh} /> : <p className="text-sm text-[#A8A090]" data-testid="account-plan-free">You're on the <b className="text-[#F3EBDD]">Free</b> plan.</p>}
      {!fixed && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="lf-label">Change plan</div>
            <div className="flex rounded-full border border-[#2D3442] p-0.5 text-xs" role="tablist">
              {[["month", "Monthly"], ["year", "Yearly"]].map(([v, l]) => (
                <button key={v} onClick={() => setPeriod(v)} className={`rounded-full px-3 py-1 ${interval === v ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`} data-testid={`account-interval-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {OPTIONS.map(([t, l]) => {
              const cur = isCurrent(t);
              const disabled = cur || (t === "free" && (!subscribed || sub?.cancel_at_period_end)) || (b.lifetime === "pro" && t !== "commercial") || !!busy;
              return (
                <button key={t} disabled={disabled} onClick={() => pick(t)} className={`rounded-md border p-4 text-left transition-colors ${cur ? "border-[#D97706] bg-[#1A1508]" : "border-[#2D3442] bg-[#0B0D10] hover:border-[#D97706]/60"} disabled:cursor-default disabled:opacity-70`} data-testid={`account-plan-option-${t}`}>
                  <div className="text-sm font-medium text-[#F3EBDD]">{l}</div>
                  <div className="text-xs text-[#A8A090]">{t === "free" ? "$0 forever" : `${usd(price(t))} / ${interval === "year" ? "year" : "month"}`}</div>
                  <div className="mt-2 text-xs text-[#FBBF24]">{busy === t ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : cur ? "Current plan" : ""}</div>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-[#A8A090]">Upgrades start right away, and unused time on your current plan is credited. Downgrades wait until the time you've paid for runs out. <Link to="/pricing" className="text-[#F59E0B] hover:underline" data-testid="account-all-plans-link">See all plans, custom plans and lifetime options</Link>.</p>
        </div>
      )}
      {change && <ChangePlanDialog target={change} sub={sub} onClose={() => setChange(null)} onDone={refresh} />}
    </div>
  );
};
